/**
 * 和歌山県の背景ツアー「和歌山めぐり」コンテンツ。
 *
 * 静かに扱う景色（solemn）は高野山 奥之院の参道。20万基をこえる墓碑が並ぶ墓域で、
 * 弘法大師御廟へ向かう祈りの道でもある。花火・流れ星・画面の揺れを止める。
 *
 * 軸は一日の時間。串本の日の出から、白浜の日没へ。
 * 熊野三山と高野山にそれぞれ2か所ずつを割き、残りを紀北・有田・湯浅・白浜に配している。
 *
 * クジラについて。
 *  料理も景色も、鯨に関するものは一切入れない。太地町も扱わない。
 *  これは作品全体の方針で、和歌山だけの判断ではない。
 *
 * 高野山 奥之院について。
 *  一の橋から御廟までの参道は20万基をこえる墓碑が並ぶ墓域。
 *  驚かせる書き方・怪談めいた書き方をしない。静かな場所として扱う。
 *  御廟橋から先は撮影禁止なので、絵にも御廟そのものを出さない。
 *
 * 那智の滝について。
 *  飛瀧神社の御神体そのもの。落差の数値は熊野那智大社公式の「高さ133メートル」を使う。
 *  「落差日本一」とは書かない。一段の滝としての比較は公式で確認できなかった。
 *  三重塔と滝を一緒に写す構図は那智勝浦のいちばん知られた景で、これを主題にする。
 *
 * 大斎原について。
 *  熊野本宮大社の旧社地。明治22年の水害で流失し、現在の社殿は山の上に遷っている。
 *  大鳥居は平成12年に建てられたもので、ここは鳥居を描いてよい数少ない景。
 *
 * そのほか調査で出てきた「書いてはいけないこと」。
 *  - 生石高原のススキは奈良の曽爾高原と絵がぶつかるので入れない。代わりに和歌山城 御橋廊下を置く。
 *  - 和歌山城の天守は昭和33年の再建。「現存天守」と書かない。
 *  - 友ヶ島をアニメ作品と結びつけて書かない。
 *  - 橋杭岩の岩柱の数と長さは串本町・県観光公式の表現をそのまま使い、丸めない。
 *  - 湯浅の重伝建は「醸造町」の種別で、選定は平成18年12月19日、6.3ヘクタール。和歌山県では唯一。
 *  - 拝観料・開館時間・祭礼の日程など、時間で変わるものは書かない。
 */

import type { StageTour } from '../types';

export const wakayamaSceneryTour: StageTour = {
  scenes: [
    {
      id: 'hashiguiiwa',
      name: '橋杭岩',
      area: '串本町・日の出',
      image: '/assets/scenery/wakayama/scenes/01_hashiguiiwa.webp',
      hotspots: [
        {
          id: 'hashiguiiwa-retsu',
          x: 0.5,
          y: 0.46,
          title: '海に一列に並ぶ岩の柱',
          body: '串本の海岸から紀伊大島の方向へ、大小約40の岩柱がおよそ850メートルにわたって一列に並びます。橋の杭が並んでいるように見えることからこの名がつきました。',
          source: 'https://www.town.kushimoto.wakayama.jp/kanko/kushimoto/index.html',
          mapQuery: '橋杭岩',
        },
        {
          id: 'hashiguiiwa-meisho',
          x: 0.24,
          y: 0.6,
          title: '国の名勝・天然記念物',
          body: '橋杭岩は国の名勝および天然記念物に指定されています。やわらかい泥岩の層に硬い岩がしみ込んで固まり、まわりだけが波に削られて硬い部分が柱として残りました。',
          source: 'https://www.town.kushimoto.wakayama.jp/kanko/kushimoto/index.html',
          mapQuery: '橋杭岩 名勝',
        },
        {
          id: 'hashiguiiwa-asahi',
          x: 0.76,
          y: 0.3,
          title: '朝日の名所',
          body: '橋杭岩は、特に朝日が昇る瞬間が美しいとされています。水平線から現れる太陽が、岩と海を黄金色に染め上げます。',
          source: 'https://www.wakayama-kanko.or.jp/spots/detail_514.html',
          mapQuery: '橋杭岩 日の出',
        },
      ],
    },
    {
      id: 'oyunohara',
      name: '大斎原',
      area: '田辺市本宮町・朝霧',
      image: '/assets/scenery/wakayama/scenes/02_oyunohara.webp',
      hotspots: [
        {
          id: 'oyunohara-kyuushachi',
          x: 0.5,
          y: 0.52,
          title: '熊野本宮大社の旧社地',
          body: '熊野川・音無川・岩田川が合流する中洲にあり、もとはここに熊野本宮大社の社殿が建っていました。明治22年（1889年）8月の大水害で社殿の多くが流され、水害をまぬがれた4社が現在の熊野本宮大社の場所へ遷されました。',
          source: 'https://www.hongu.jp/kumanokodo/hongu-taisya/ooyunohara/',
          mapQuery: '大斎原',
        },
        {
          id: 'oyunohara-otorii',
          x: 0.5,
          y: 0.26,
          title: '高さ約34メートルの大鳥居',
          body: '入口に立つ大鳥居は高さ約34メートル、幅約42メートル。現在の熊野本宮大社からも、道路を隔てた先にその姿が見えます。',
          source: 'https://www.hongu.jp/kumanokodo/hongu-taisya/ooyunohara/',
          mapQuery: '大斎原 大鳥居',
        },
        {
          id: 'oyunohara-kumanokodo',
          x: 0.22,
          y: 0.66,
          title: '歩いて川を渡った参拝者',
          body: 'かつて中洲へは江戸時代まで橋がかけられず、参拝に訪れた人々は歩いて川を渡りました。音無川の冷たい水で最後の水垢離を行い、身を清めてから社へ向かったといいます。',
          source: 'https://www.hongu.jp/kumanokodo/hongu-taisya/ooyunohara/',
          mapQuery: '熊野本宮大社',
        },
      ],
    },
    {
      id: 'daimonzaka',
      name: '大門坂',
      area: '那智勝浦町・午前',
      image: '/assets/scenery/wakayama/scenes/03_daimonzaka.webp',
      hotspots: [
        {
          id: 'daimonzaka-ishidatami',
          x: 0.5,
          y: 0.58,
          title: '約270段の石畳',
          body: '大門坂は全長約500メートル、高低差約100メートル、およそ270段の苔むした石畳道です。樹齢800年を超す老杉に囲まれ、熊野古道の面影をもっとも色濃く今に残している場所とされています。',
          source: 'https://www.wakayama-kanko.or.jp/courses/detail_34.html',
          mapQuery: '大門坂',
        },
        {
          id: 'daimonzaka-meotosugi',
          x: 0.28,
          y: 0.34,
          title: '夫婦杉',
          body: '大門坂には、樹齢800余年を経た夫婦杉が立っています。坂は昔の石畳の姿のまま今も残り、国指定の歴史の道として保護されています。',
          source: 'https://kumanonachitaisha.or.jp/pavilion/mountain/',
          mapQuery: '大門坂 夫婦杉',
        },
        {
          id: 'daimonzaka-kumanokodo',
          x: 0.74,
          y: 0.44,
          title: '熊野古道 中辺路',
          body: '大門坂の道沿いには、熊野古道中辺路の最後の王子社とされる多富気王子があります。坂を上りきると熊野那智大社と青岸渡寺があり、その先に那智の滝が続きます。',
          source: 'https://www.wakayama-kanko.or.jp/courses/detail_34.html',
          mapQuery: '熊野古道 中辺路 大門坂',
        },
      ],
    },
    {
      id: 'nachi-no-taki',
      name: '那智の滝と三重塔',
      area: '那智勝浦町・昼前',
      image: '/assets/scenery/wakayama/scenes/04_nachi_no_taki.webp',
      hotspots: [
        {
          id: 'nachi-taki-takasa',
          x: 0.68,
          y: 0.44,
          title: '高さ133メートル',
          body: '那智の滝は高さ133メートル、銚子口の幅13メートル、滝壺の深さは10メートル。落ちる水の量は毎秒1トンほどです。熊野那智大社が公式に示している数値です。',
          source: 'https://kumanonachitaisha.or.jp/pavilion/waterfall/',
          mapQuery: '那智の滝',
        },
        {
          id: 'nachi-taki-goshintai',
          x: 0.42,
          y: 0.62,
          title: '滝そのものが御神体',
          body: '滝を祀る飛瀧神社には社殿がなく、滝そのものが御神体としてお祀りされています。一の滝から三の滝までの三つの滝が、国の名勝に指定されています。',
          source: 'https://kumanonachitaisha.or.jp/pavilion/waterfall/',
          mapQuery: '飛瀧神社',
        },
        {
          id: 'nachi-sanjunoto',
          x: 0.2,
          y: 0.36,
          title: '三重塔と滝の眺め',
          body: '青岸渡寺の本堂の後方からは、那智の滝と朱塗りの三重塔の美しい調和が楽しめます。塔の上層階からは、那智の滝や太平洋の広大な景色を望むことができます。',
          source: 'https://www.wakayama-kanko.or.jp/spots/detail_501.html',
          mapQuery: '青岸渡寺 三重塔',
        },
      ],
    },
    {
      id: 'koyasan-danjogaran',
      name: '高野山 壇上伽藍',
      area: '高野町・正午',
      image: '/assets/scenery/wakayama/scenes/05_koyasan_danjogaran.webp',
      hotspots: [
        {
          id: 'danjogaran-konpon-daito',
          x: 0.5,
          y: 0.44,
          title: '朱色の根本大塔',
          body: '壇上伽藍は、弘法大師空海が高野山を開いた際に最初に整備した場所です。境内には総本堂の金堂や、密教のシンボルとして建てられた朱塗りの根本大塔が建っています。',
          source: 'https://www.wakayama-kanko.or.jp/spots/detail_481.html',
          mapQuery: '高野山 根本大塔',
        },
        {
          id: 'danjogaran-sekaiisan',
          x: 0.24,
          y: 0.6,
          title: '世界遺産「紀伊山地の霊場と参詣道」',
          body: '高野山は、熊野三山・吉野・大峯とともに、世界遺産「紀伊山地の霊場と参詣道」を構成しています。',
          source: 'https://www.town.yoshino.nara.jp/promotion/kankojoho/sekaiisan/index.html',
          mapQuery: '高野山',
        },
        {
          id: 'danjogaran-yama',
          x: 0.76,
          y: 0.3,
          title: '山上に開けた町',
          body: '高野山は、標高約1000メートル級の峰々に囲まれた山上の盆地に広がっています。117もの寺院が立ち並び、そのうち50ほどが宿坊として旅行者を泊めています。夏は涼しく過ごしやすい避暑地として知られています。',
          source: 'https://www.wakayama-kanko.or.jp/features/koyasan',
          mapQuery: '高野山 壇上伽藍',
        },
      ],
    },
    {
      id: 'koyasan-okunoin',
      name: '高野山 奥之院の参道',
      area: '高野町・午後',
      image: '/assets/scenery/wakayama/scenes/06_koyasan_okunoin.webp',
      solemn: true,
      hotspots: [
        {
          id: 'okunoin-sando',
          x: 0.5,
          y: 0.52,
          title: '一の橋からの約2キロ',
          body: '奥之院の参道は一の橋から弘法大師御廟まで約2キロ続きます。途中に一の橋・中の橋・御廟橋の三つの橋があり、橋を渡るごとに奥へ進んでいきます。',
          source: 'https://www.wakayama-kanko.or.jp/spots/detail_515.html',
          mapQuery: '高野山 奥之院',
        },
        {
          id: 'okunoin-bohi',
          x: 0.26,
          y: 0.62,
          title: '20万基をこえる墓碑',
          body: '参道の両側には、20万基をこえる墓碑や供養塔が並びます。戦国武将から皇族、庶民まで、さまざまな人の墓碑が、同じ杉林のなかに静かに置かれています。',
          source: 'https://www.wakayama-kanko.or.jp/spots/detail_515.html',
          mapQuery: '高野山 奥之院 参道',
        },
        {
          id: 'okunoin-sugi',
          x: 0.74,
          y: 0.28,
          title: '樹齢700年をこえる杉',
          body: '参道は樹齢700年をこえる杉林に囲まれています。中の橋を過ぎ、御廟橋を渡ると、弘法大師の御廟のある聖域に入ります。',
          source: 'https://www.wakayama-kanko.or.jp/spots/detail_515.html',
          mapQuery: '高野山 奥之院 杉並木',
        },
      ],
    },
    {
      id: 'yuasa-machinami',
      name: '湯浅の町並み',
      area: '湯浅町・夕方前',
      image: '/assets/scenery/wakayama/scenes/07_yuasa_machinami.webp',
      hotspots: [
        {
          id: 'yuasa-judenken',
          x: 0.5,
          y: 0.5,
          title: '重要伝統的建造物群保存地区',
          body: '湯浅は「醸造町」の種別で、平成18年12月19日に重要伝統的建造物群保存地区に選定されました。面積は約6.3ヘクタールで、和歌山県ではここだけです。',
          source:
            'https://www.bunka.go.jp/seisaku/bunkazai/shokai/hozonchiku/judenken_ichiran.html',
          mapQuery: '湯浅 重要伝統的建造物群保存地区',
        },
        {
          id: 'yuasa-shoyu',
          x: 0.24,
          y: 0.36,
          title: '醤油醸造の町',
          body: '湯浅は醤油の醸造業で栄えた町です。重厚な瓦葺きの屋根と繊細な格子が印象的な町家や、白壁の土蔵が通りに建ち並びます。',
          source: 'https://www.town.yuasa.wakayama.jp/site/rekishi-bunka/5887.html',
          mapQuery: '湯浅町 醤油',
        },
        {
          id: 'yuasa-nihon-isan',
          x: 0.76,
          y: 0.6,
          title: '日本遺産「最初の一滴」',
          body: '「『最初の一滴』醤油醸造の発祥の地 紀州湯浅」として、平成29年4月28日に日本遺産に認定されています。',
          source: 'https://www.town.yuasa.wakayama.jp/site/rekishi-bunka/5887.html',
          mapQuery: '湯浅 日本遺産',
        },
      ],
    },
    {
      id: 'aragijima',
      name: 'あらぎ島',
      area: '有田川町・秋の午後',
      image: '/assets/scenery/wakayama/scenes/08_aragijima.webp',
      hotspots: [
        {
          id: 'aragijima-ogi',
          x: 0.5,
          y: 0.54,
          title: '扇の形の棚田',
          body: '有田川に沿って弧を描く、扇形とも表現される棚田です。この地方では古くから川沿いの耕地を「島」と呼ぶことがあり、あらぎ島（蘭島）の名もそこから来ています。',
          source: 'https://www.town.aridagawa.lg.jp/aragijimakeikan/introduction.html',
          mapQuery: 'あらぎ島',
        },
        {
          id: 'aragijima-bunkateki-keikan',
          x: 0.26,
          y: 0.34,
          title: '国の重要文化的景観',
          body: '平成25年10月17日、あらぎ島と周囲の景観が「蘭島及び三田・清水の農山村景観」として国の重要文化的景観に選定されました。人が暮らしのために自然に働きかけ、長い年月をかけて形づくってきた風景です。',
          source: 'https://www.town.aridagawa.lg.jp/aragijimakeikan/index.html',
          mapQuery: 'あらぎ島 重要文化的景観',
        },
        {
          id: 'aragijima-tenbo',
          x: 0.76,
          y: 0.28,
          title: '展望台から一望する',
          body: '展望台からは、季節ごとに変わる棚田を一望できます。春は水面が鏡のように空を映し、夏は緑の絨毯、秋は黄金色の稲穂、冬は真っ白な雪景色になります。',
          source: 'https://www.wakayama-kanko.or.jp/spots/detail_33.html',
          mapQuery: 'あらぎ島 展望台',
        },
      ],
    },
    {
      id: 'wakayamajo-ohashiroka',
      name: '和歌山城 御橋廊下',
      area: '和歌山市・夕',
      image: '/assets/scenery/wakayama/scenes/09_wakayamajo_ohashiroka.webp',
      hotspots: [
        {
          id: 'ohashiroka-naname',
          x: 0.46,
          y: 0.5,
          title: '斜めにかかる橋',
          body: '御橋廊下は、西の丸と二の丸大奥とをつなぐ橋です。両岸の高低差のため斜めにかかっていて、全国的にも珍しい形の橋です。',
          source: 'https://www.wakayama-kanko.or.jp/spots/detail_661.html',
          mapQuery: '和歌山城 御橋廊下',
        },
        {
          id: 'ohashiroka-ishigaki',
          x: 0.22,
          y: 0.62,
          title: '城主ごとにちがう石垣',
          body: '和歌山城の石垣は築かれた時期によって積み方がちがい、城主が代わるたびに手が入ったことが今も見てわかります。',
          source: 'https://www.wakayama-kanko.or.jp/spots/detail_661.html',
          mapQuery: '和歌山城 石垣',
        },
        {
          id: 'ohashiroka-tenshu',
          x: 0.74,
          y: 0.3,
          title: '昭和33年再建の天守',
          body: '奥に見える白亜の三層の大天守は、昭和33年（1958年）に再建されたものです。和歌山城は、8代将軍吉宗と14代将軍家茂を出した紀州徳川家の居城でした。',
          source: 'https://www.wakayama-kanko.or.jp/spots/detail_661.html',
          mapQuery: '和歌山城',
        },
      ],
    },
    {
      id: 'engetsuto',
      name: '円月島',
      area: '白浜町・日没',
      image: '/assets/scenery/wakayama/scenes/10_engetsuto.webp',
      hotspots: [
        {
          id: 'engetsuto-ana',
          x: 0.5,
          y: 0.48,
          title: '中央に開いた円い穴',
          body: '島の中央に円月形の海蝕洞が開いていることから円月島と呼ばれます。正式な名前は高嶋といいます。',
          source: 'https://www.town.shirahama.wakayama.jp/kanko/midokoro/1454401545853.html',
          mapQuery: '円月島',
        },
        {
          id: 'engetsuto-size',
          x: 0.24,
          y: 0.58,
          title: '南北130メートルの小島',
          body: '南北130メートル、東西35メートル、高さ25メートルの小さな島です。臨海浦の南の入口に浮かんでいます。',
          source: 'https://www.town.shirahama.wakayama.jp/kanko/midokoro/1454401545853.html',
          mapQuery: '円月島 白浜',
        },
        {
          id: 'engetsuto-yuhi',
          x: 0.76,
          y: 0.34,
          title: '和歌山県の夕陽100選',
          body: '和歌山県の夕陽100選に選ばれています。白浜町によると、夕日の見頃の時刻は季節で変わり、夏は18時30分ごろ、冬は16時30分ごろです。',
          source: 'https://www.town.shirahama.wakayama.jp/kanko/midokoro/1454401545853.html',
          mapQuery: '円月島 夕日',
        },
      ],
    },
  ],
};
